<template>
    <Page behind-nav>
        <div class="procreate-section procreate-section--hero procreate-brushes-hero">
            <div class="container">
                <div class="procreate-copy procreate-copy--center procreate-copy--widest">
                    <h1 class="sr-only">{{ $t("ipad-brushes.title") }}</h1>
                    <h2 class="text-subheading" style="z-index: 1; position: relative">{{ $t("ipad-brushes.hero.subheading") }}</h2>
                    <p
                        class="text-heading text-heading--md ipad-brushes-page-title"
                        style="z-index: 1; position: relative"
                        v-html="$t('ipad-brushes.hero.heading')"
                    ></p>
                    <img :src="$asset($t('ipad-brushes.hero.image'))" alt="" width="2600" height="1920" />
                    <CreditMedia
                        artist="Eliza Ivanova"
                        artwork="Untitled"
                        :commission="true"
                        :thumbnail="$asset('images/credit-thumbnails/ipad/eliza-ivanova-untitled.jpg')"
                        credit-only
                    />
                    <div class="container container--narrow">
                        <p class="text-body" v-html="$t('ipad-brushes.hero.body')"></p>
                    </div>
                </div>
            </div>
        </div>

        <ProAnchor name="paint-smudge-erase">
            <section class="procreate-section procreate-section--alternate-bg paint-smudge-erase">
                <div class="container">
                    <div class="procreate-copy procreate-copy--widest">
                        <h2 class="text-subheading">{{ $t("ipad-brushes.smudge_erase.subheading") }}</h2>
                        <p class="text-heading text-heading--md procreate-copy__dimmable" v-html="$t('ipad-brushes.smudge_erase.body')"></p>
                    </div>
                </div>
            </section>
        </ProAnchor>

        <ProAnchor name="brush-studio">
            <section class="procreate-section">
                <div class="container">
                    <div class="procreate-copy procreate-copy--center">
                        <h2 class="text-subheading">{{ $t("ipad-brushes.brush_studio.subheading") }}</h2>
                        <p class="text-heading text-heading--md" v-html="$t('ipad-brushes.brush_studio.heading')"></p>
                        <p class="text-body" v-html="$t('ipad-brushes.brush_studio.body')"></p>
                    </div>
                </div>

                <DualIpad reverse :title="$t('ipad-brushes.brush_studio.auto_texture.heading')" :body="$t('ipad-brushes.brush_studio.auto_texture.body')">
                    <template v-slot:left>
                        <CreditMedia
                            :src="$asset($t('ipad-brushes.brush_studio.image_right'))"
                            alt=""
                            width="1300"
                            height="1734"
                            artist="Pauline Voß"
                            artwork="The Kiss"
                            :commission="true"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/pauline-vos-the-kiss.jpg')"
                            weighting="top"
                        />
                    </template>
                    <template v-slot:right
                        ><CreditMedia
                            :src="$asset($t('ipad-brushes.brush_studio.image_left'))"
                            alt=""
                            width="1734"
                            height="1300"
                            artist="Andrew Peña"
                            artwork="Echo Park"
                            :commission="true"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/andrew-pena-echo-park.jpg')"
                        />
                    </template>
                </DualIpad>
            </section>
        </ProAnchor>

        <ProAnchor name="shape-grain">
            <section class="procreate-section procreate-section--alternate-bg">
                <div class="container">
                    <div class="two-column">
                        <div class="two-column__column">
                            <img :src="$asset('images/ipad/ipad-brush-diagram.jpg')" alt="" />
                        </div>
                        <div class="two-column__column">
                            <div class="procreate-copy">
                                <h2 class="text-subheading">{{ $t("ipad-brushes.shape_grain.subheading") }}</h2>
                                <p class="text-heading" v-html="$t('ipad-brushes.shape_grain.heading')"></p>
                                <p class="text-body" v-html="$t('ipad-brushes.shape_grain.body')"></p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </ProAnchor>

        <ProAnchor name="make-brushes">
            <section class="procreate-section">
                <div class="container">
                    <div class="procreate-copy procreate-copy--center">
                        <h2 class="text-subheading">{{ $t("ipad-brushes.make-brushes.subheading") }}</h2>
                        <p class="text-heading text-heading--md" v-html="$t('ipad-brushes.make-brushes.heading')"></p>
                        <p class="text-body" v-html="$t('ipad-brushes.make-brushes.body')"></p>
                    </div>
                </div>
                <DualIpad overlap>
                    <template v-slot:right
                        ><CreditMedia
                            :src="$asset($t('ipad-brushes.make-brushes.image_right'))"
                            alt=""
                            width="1734"
                            height="1300"
                            artist="Max Ulichney"
                            artwork="Believers"
                            :commission="true"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/max-ulichney-believers.jpg')"
                        />
                    </template>
                    <template v-slot:left>
                        <img :src="$asset($t('ipad-brushes.make-brushes.image_left'))" alt="" width="1734" height="1300" />
                    </template>
                </DualIpad>
            </section>
        </ProAnchor>

        <ProAnchor name="dual-brushes">
            <section class="procreate-section">
                <SingleIpad reverseSM>
                    <template v-slot:ipad>
                        <CreditMedia
                            :src="$asset($t('ipad-brushes.dual_brush.image'))"
                            alt=""
                            width="1734"
                            height="1300"
                            artist="Arash Razavi"
                            artwork="Tawny"
                            :commission="true"
                            weighting="top"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/arash-razavi-tawny.jpg')"
                        />
                    </template>

                    <div class="procreate-copy procreate-copy--center-sm">
                        <h2 class="text-subheading">{{ $t("ipad-brushes.dual_brush.subheading") }}</h2>
                        <p class="text-heading text-heading--md" v-html="$t('ipad-brushes.dual_brush.heading')"></p>
                        <p class="text-body" v-html="$t('ipad-brushes.dual_brush.body')"></p>
                    </div>
                </SingleIpad>
            </section>
        </ProAnchor>

        <ProAnchor name="import-brushes">
            <section class="procreate-section">
                <div class="container">
                    <div class="procreate-copy procreate-copy--center">
                        <h2 class="text-subheading">{{ $t("ipad-brushes.photoshop.subheading") }}</h2>
                        <p class="text-heading text-heading--md" v-html="$t('ipad-brushes.photoshop.heading')"></p>
                        <p class="text-body" v-html="$t('ipad-brushes.photoshop.body')"></p>
                    </div>
                </div>
                <SingleIpad>
                    <template v-slot:ipad>
                        <CreditMedia
                            :src="$asset($t('ipad-brushes.photoshop.image'))"
                            alt=""
                            width="1734"
                            height="1300"
                            artist="Rebecca Mills"
                            artwork="Little Help!"
                            :commission="false"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/rebecca-mills-little-help.jpg')"
                        />
                    </template>
                </SingleIpad>
            </section>
        </ProAnchor>

        <ProAnchor name="brush-library">
            <section class="procreate-section">
                <SingleIpad reverseSM>
                    <template v-slot:ipad>
                        <CreditMedia
                            :src="$asset($t('ipad-brushes.brush_library.image'))"
                            alt=""
                            width="1734"
                            height="1300"
                            :artist="$t('ipad-brushes.brush_library.artist')"
                            :artwork="$t('ipad-brushes.brush_library.artwork')"
                            :commission="true"
                            :thumbnail="$asset($t('ipad-brushes.brush_library.thumbnail'))"
                        />
                    </template>

                    <div class="procreate-copy procreate-copy--center-sm">
                        <h2 class="text-subheading">{{ $t("ipad-brushes.brush_library.subheading") }}</h2>
                        <p class="text-heading text-heading--md" v-html="$t('ipad-brushes.brush_library.heading')"></p>
                        <p class="text-body" v-html="$t('ipad-brushes.brush_library.body')"></p>
                    </div>
                </SingleIpad>
            </section>
        </ProAnchor>

        <!-- Stroke Stabilizer-->
        <ProAnchor name="stroke-stablizer">
            <section class="procreate-section">
                <div class="container">
                    <div class="procreate-copy procreate-copy--center">
                        <h2 class="text-subheading">{{ $t("ipad-brushes.stroke_stabilizer.subheading") }}</h2>
                        <p class="text-heading text-heading--md">{{ $t("ipad-brushes.stroke_stabilizer.heading") }}</p>
                        <p class="text-body" v-html="$t('ipad-brushes.stroke_stabilizer.body')"></p>
                    </div>
                </div>
                <DualIpad>
                    <template v-slot:left>
                        <CreditMedia
                            :src="$asset($t('ipad-brushes.stroke_stabilizer.image_left'))"
                            alt=""
                            width="1300"
                            height="1734"
                            artist="Kael Ngu"
                            artwork="Escape"
                            :commission="true"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/kael-escape.jpg')"
                        />
                    </template>
                    <template v-slot:right>
                        <img :src="$asset($t('ipad-brushes.stroke_stabilizer.image_right'))" alt="" width="1734" height="1300" />
                    </template>
                </DualIpad>
            </section>
        </ProAnchor>

        <!-- Recent Brushes -->
        <ProAnchor name="recent-brushes">
            <section class="procreate-section">
                <SingleIpad reverseSM>
                    <template v-slot:ipad>
                        <CreditMedia
                            :src="$asset($t('ipad-brushes.recent_brushes.image'))"
                            alt=""
                            width="1734"
                            height="1300"
                            artist="Acky Bright"
                            artwork="Attack"
                            :commission="true"
                            :thumbnail="$asset('images/credit-thumbnails/ipad/christiane-king-another-untitled.jpg')"
                        />
                    </template>
                    <div class="procreate-copy procreate-copy procreate-copy--center-sm">
                        <h2 class="text-subheading">{{ $t("ipad-brushes.recent_brushes.leader") }}</h2>
                        <p class="text-heading text-heading--md" v-html="$t('ipad-brushes.recent_brushes.heading')"></p>
                        <p class="text-body" v-html="$t('ipad-brushes.recent_brushes.body')"></p>
                    </div>
                </SingleIpad>
            </section>
        </ProAnchor>

        <ProAnchor name="apple-pencil">
            <section class="procreate-section procreate-section--alternate-bg">
                <div class="procreate-apple-pencil">
                    <div class="container container--narrow">
                        <div class="procreate-copy procreate-copy--center">
                            <h2 class="text-subheading">{{ $t("ipad-brushes.apple_pencil.subheading") }}</h2>
                            <p class="text-heading text-heading--md">{{ $t("ipad-brushes.apple_pencil.heading") }}</p>
                            <div class="procreate-apple-pencil__container">
                                <img :src="$asset('images/pencil/pencil-flattened.png')" alt="" />
                            </div>
                            <p class="text-body" v-html="$t('ipad-brushes.apple_pencil.body')"></p>
                        </div>
                    </div>
                </div>
            </section>
        </ProAnchor>

        <section class="procreate-section">
            <div class="container container--narrow" v-fade>
                <IpadBuyNow ctaLink="https://apps.apple.com/app/apple-store/id425073498?pt=345446&ct=Website_ipad-brushes&mt=8" />
            </div>
        </section>

        <ArtistCredits />
    </Page>
</template>

<script>
import IpadBuyNow from "@/components/ipad/IpadBuyNow";
import DualIpad from "@/components/ipad/DualIpad";
import SingleIpad from "@/components/ipad/SingleIpad";
import CreditMedia from "@/components/CreditMedia";
import ArtistCredits from "@/components/ArtistCredits";
import { metaTitles, metaDescriptions, metaImages, metaKeywords } from "~/utils/meta";
import { procreateSoftwareApplication, createJsonLdWrapper } from "@/utils/schema.org.js";

export default {
    components: {
        IpadBuyNow,
        DualIpad,
        SingleIpad,
        CreditMedia,
        ArtistCredits,
    },

    head() {
        const i18nSeo = this.$nuxtI18nSeo();

        return {
            title: this.$t("ipad-brushes.meta.title"),
            script: [createJsonLdWrapper(procreateSoftwareApplication)],
            meta: [
                ...metaTitles(this.$t("ipad-brushes.meta.title")),
                ...metaDescriptions(this.$t("ipad-brushes.meta.description")),
                ...metaImages(this.$asset(this.$t("ipad-brushes.meta.image"))),
                ...metaKeywords(this.$t("ipad-brushes.meta.keywords")),
                ...i18nSeo.meta,
            ],
            link: [...i18nSeo.link],
        };
    },
};
</script>
